import React, { useState, useRef, useEffect, useCallback } from 'react';
import s from './s.module.less';
import { EAppointmentContactTypeDictKey } from 'types/common';
import dayjs from 'dayjs';
import { getCalendarAppointmentEventDetail } from 'api/google';
import { TServerCalendarAppointmentEventDetail } from 'types/calendar';
import { getTimeStr } from 'utils/calendar';
import { PATH } from 'constants/path';
import { Button } from 'antd';
import capitalize from 'lodash/capitalize';

interface AppointmentCardProps {
    id: number;
    contactType: EAppointmentContactTypeDictKey;
    // status?: string;
    // title: string;
    // date: number;
    startAt: string;
    endAt: string;
    telephone?: string;
    // time: number;
    duration: number;
    teleHealthLink: string;
    visitReason: string;
    actualDuration: number;
    appointmentName: string;
    appointmentStatus: string;
    notes?: string;
    reason?: string;
    location?: string;
}

const AppointmentCard: React.FC<AppointmentCardProps> = ({
    appointmentStatus = 'Scheduled',
    appointmentName,
    duration,
    teleHealthLink,
    reason,
    startAt,
    endAt,
    telephone,
    contactType,
    id,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [isOverflow, setIsOverflow] = useState(false);
    const visitRef = useRef<HTMLDivElement>(null);
    const hiddenRef = useRef<HTMLDivElement>(null);
    const [viewAppointmentLoading, setViewAppointmentLoading] = useState(false);

    useEffect(() => {
        if (hiddenRef.current && visitRef.current) {
            setIsOverflow(hiddenRef.current.offsetWidth > visitRef.current.offsetWidth);
        }
    }, [reason]);

    const handleClickViewAppointment = useCallback(async (appointmentId: number) => {
        if (!appointmentId) {
            return;
        }
        setViewAppointmentLoading(true);
        const res = await getCalendarAppointmentEventDetail(`${appointmentId}`);
        setViewAppointmentLoading(false);
        if (!res.error) {
            const resData: TServerCalendarAppointmentEventDetail | undefined = res?.data?.data || undefined;
            if (resData) {
                window.open(`${PATH.DASHBOARD}/${PATH.CALENDAR}?aid=${resData?.id}&date=${getTimeStr(dayjs(resData?.startAt), 'DD-MM-YYYY')}`, '_blank');
            }
        }
    }, []);

    return (
        <div className={s.card}>
            <div className={s.header}>
                <span
                    className={s.status}
                    style={appointmentStatus !== 'scheduled' ? { color: 'var(--red-800, #991b1b)', backgroundColor: 'var(--red-100, #fee2e2)' } : {}}
                >
                    {capitalize(appointmentStatus)}
                </span>
                <Button className={s.viewBtn} loading={viewAppointmentLoading} onClick={() => handleClickViewAppointment(id)}>View</Button>
            </div>
            <div className={s.title}>{appointmentName}</div>
            <div className={s.row}>
                <span className={s.icon}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M4.66667 4.08333V1.75M9.33333 4.08333V1.75M4.08333 6.41667H9.91667M2.91667 12.25H11.0833C11.7277 12.25 12.25 11.7277 12.25 11.0833V4.08333C12.25 3.439 11.7277 2.91667 11.0833 2.91667H2.91667C2.27233 2.91667 1.75 3.439 1.75 4.08333V11.0833C1.75 11.7277 2.27233 12.25 2.91667 12.25Z" stroke="#6B7280" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </span>
                <span>{dayjs(startAt || '').format('dddd, MMMM D, YYYY')}</span>
            </div>
            <div className={s.row}>
                <span className={s.icon}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M7 4.66667V7L8.75 8.75M12.25 7C12.25 9.8995 9.8995 12.25 7 12.25C4.10051 12.25 1.75 9.8995 1.75 7C1.75 4.10051 4.10051 1.75 7 1.75C9.8995 1.75 12.25 4.10051 12.25 7Z" stroke="#6B7280" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </span>
                <span>{`${dayjs(startAt || '').format('H:mm')} - ${dayjs(endAt || '').format('H:mm a')} (PST)`} | {duration} mins</span>
            </div>
            <div className={s.row}>
                <span className={s.icon}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M10.2995 9.7165C9.75315 10.2629 8.61059 11.4054 7.82439 12.1916C7.36878 12.6472 6.63082 12.6475 6.17521 12.1919C5.40271 11.4194 4.28227 10.2989 3.69984 9.7165C1.8774 7.89405 1.8774 4.93928 3.69984 3.11684C5.52229 1.29439 8.47706 1.29439 10.2995 3.11684C12.122 4.93928 12.122 7.89405 10.2995 9.7165Z" stroke="#6B7280" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8.74967 6.41667C8.74967 7.38317 7.96617 8.16667 6.99967 8.16667C6.03318 8.16667 5.24967 7.38317 5.24967 6.41667C5.24967 5.45017 6.03318 4.66667 6.99967 4.66667C7.96617 4.66667 8.74967 5.45017 8.74967 6.41667Z" stroke="#6B7280" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </span>
                {
                    contactType === EAppointmentContactTypeDictKey.VIDEO ? (
                        <span className={s.contactTypeWrap}>Video: <a className={`${s.link} ${s.linkVideo}`} href={teleHealthLink} target="_blank" rel="noopener noreferrer">{teleHealthLink}</a> {teleHealthLink && <div className={s.linkExtraIcon} />}</span>
                    ) : (
                        <span className={s.contactTypeWrap}>Tele-health: {telephone}</span>
                    )
                }
            </div>
            <div className={s.row}>
                <div className={s.icon}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M5.25033 7H8.75033M5.25033 9.33333H8.75033M9.91699 12.25H4.08366C3.43933 12.25 2.91699 11.7277 2.91699 11.0833V2.91667C2.91699 2.27233 3.43933 1.75 4.08366 1.75H7.34204C7.49674 1.75 7.64512 1.81146 7.75451 1.92085L10.9128 5.07915C11.0222 5.18854 11.0837 5.33692 11.0837 5.49162V11.0833C11.0837 11.7277 10.5613 12.25 9.91699 12.25Z" stroke="#6B7280" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <div
                    className={expanded ? s.visitExpanded : s.visit}
                    ref={visitRef}
                    title={reason}
                    style={{
                        width: isOverflow ? '160px' : 'auto',
                    }}
                >
                    Visit reason: {reason}
                </div>
                {
                    !expanded && isOverflow && (
                        <a className={s.link} onClick={() => setExpanded(true)}> View all</a>
                    )
                }
                <div
                    className={s.hiddenContent}
                    ref={hiddenRef}
                    title={reason}
                >
                    {reason}
                </div>
            </div>
        </div>
    );
};

export default AppointmentCard;
